import { FunctionComponent, useState } from "react";
import {
    Box,
    Button,
    Modal,
    SpaceBetween,
    Table,
    PropertyFilter,
    Pagination,
    CollectionPreferences,
    Toggle,
} from "@amzn/awsui-components-react";
import { getString, getPropertyFilterString } from "common/ui-string-labels/ui-strings-utils";
import { getBulkVendors } from "common/utils/vendor-utils";
import { BulkOrderSource, FILTERING_PROPERTIES } from "components/BulkOrdersTemplates/interfaces";
import { buildColumnDefinitions, buildVisibleContentOptions, defaultPagingOptions, deriveTrackBy, useSelectedOnlyView } from "common/utils";
import { useBulkOrdersTemplates } from "hooks/useBulkOrdersTemplates";
import { CurrentPage } from "interfaces";

const bulkVendors = getBulkVendors();

// Step 1: Vendor Selection

interface VendorSelectionStepProps {
    onSelectVendor: (vendor: string) => void;
}

export const VendorSelectionStep: FunctionComponent<VendorSelectionStepProps> = ({ onSelectVendor }) => (
    <Box textAlign="center" padding={{ vertical: "l" }}>
        <SpaceBetween size="m" alignItems="center">
            <Box variant="h3">{getString("bulkOrders.submitModal.vendor")}</Box>
            <SpaceBetween direction="horizontal" size="m">
                {bulkVendors.map((v) => (
                    <Button key={v.id} data-testid={`add-bulk-orders-vendor-${v.id}`} variant="primary" onClick={() => onSelectVendor(v.id)}>
                        {v.label}
                    </Button>
                ))}
            </SpaceBetween>
        </SpaceBetween>
    </Box>
);

// Step 2: Template Selection

interface TemplateSelectionStepProps {
    selectedVendor: string;
    selectedItems: BulkOrderSource[];
    onVendorChange: (vendor: string) => void;
    onSelectionChange: (items: BulkOrderSource[]) => void;
}

export const TemplateSelectionStep: FunctionComponent<TemplateSelectionStepProps> = ({
    selectedVendor,
    selectedItems,
    onVendorChange,
    onSelectionChange,
}) => {
    const [currentPageIndex, setCurrentPageIndex] = useState<CurrentPage>({ index: 1, isPageReset: true });

    const {
        allItems,
        loading: templatesLoading,
        filterQuery,
        setFilterQuery,
        currentPreferences,
        onPreferencesConfirmHandler,
        columns,
    } = useBulkOrdersTemplates({
        preferencesKey: 'BulkOrdersModalTemplatesPreferences',
        vendor: selectedVendor,
        status: true,
        defaultPageSize: 10,
    });

    const pageSize = currentPreferences.pageSize!;
    const pagesCountAll = Math.max(1, Math.ceil(allItems.length / pageSize));
    const effectivePageAll = Math.min(currentPageIndex.index, pagesCountAll);
    const currentPageItems = allItems.slice((effectivePageAll - 1) * pageSize, effectivePageAll * pageSize);

    const { showSelectedOnly, setShowSelectedOnly, displayedItems, pagesCount } = useSelectedOnlyView<BulkOrderSource>({
        selectedItems,
        pageSize,
        currentPageIndex,
        setCurrentPageIndex,
        fallbackItems: currentPageItems,
        fallbackTotalResults: allItems.length,
    });

    const trackBy = deriveTrackBy<BulkOrderSource>(columns);

    const VISIBLE_CONTENT_OPTIONS = buildVisibleContentOptions(columns);

    return (
        <SpaceBetween size="m">
            <Box textAlign="center">
                <SpaceBetween direction="horizontal" size="xs">
                    {bulkVendors.map((v) => (
                        <Button
                            key={v.id}
                            variant={selectedVendor === v.id ? "primary" : "normal"}
                            onClick={() => onVendorChange(v.id)}
                        >
                            {v.label}
                        </Button>
                    ))}
                </SpaceBetween>
            </Box>

            {selectedItems.length > 0 && (
                <Box data-testid="selection-count-badge">
                    <SpaceBetween direction="horizontal" size="s">
                        <span>{getString("bulkOrders.submitModal.ordersSelected", { count: String(selectedItems.length) })}</span>
                        <Toggle
                            data-testid="add-bulk-orders-show-selected-only-toggle"
                            checked={showSelectedOnly}
                            onChange={({ detail }) => setShowSelectedOnly(detail.checked)}
                        >
                            {getString("bulkOrders.submitModal.showSelectedOnlyToggle")}
                        </Toggle>
                    </SpaceBetween>
                </Box>
            )}

            <Table<BulkOrderSource>
                variant="borderless"
                selectionType="multi"
                trackBy={trackBy}
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                columnDefinitions={buildColumnDefinitions(columns, currentPreferences.visibleContent)}
                items={displayedItems ?? []}
                loading={templatesLoading}
                loadingText={getString("common.loading")}
                filter={
                    <PropertyFilter
                        query={filterQuery}
                        onChange={({ detail }) => setFilterQuery(detail)}
                        filteringProperties={FILTERING_PROPERTIES}
                        i18nStrings={getPropertyFilterString()}
                        filteringPlaceholder={getString("bulkOrders.table.filterPlaceholder")}
                        disabled={showSelectedOnly}
                        expandToViewport
                    />
                }
                empty={
                    <Box textAlign="center" color="text-status-inactive">
                        {getString("bulkOrders.submitModal.noTemplates", { vendor: selectedVendor })}
                    </Box>
                }
                pagination={
                    <Pagination
                        currentPageIndex={Math.min(currentPageIndex.index, pagesCount) || 1}
                        pagesCount={pagesCount}
                        onChange={({ detail }) => setCurrentPageIndex({ index: detail.currentPageIndex, isPageReset: false })}
                    />
                }
                preferences={
                    <CollectionPreferences
                        title={getString("common.preferences.title")}
                        confirmLabel={getString("common.preferences.confirmLabel")}
                        cancelLabel={getString("common.preferences.cancelLabel")}
                        preferences={currentPreferences}
                        onConfirm={onPreferencesConfirmHandler}
                        pageSizePreference={{
                            title: getString("common.preferences.pageSize.title"),
                            options: defaultPagingOptions,
                        }}
                        visibleContentPreference={{
                            title: getString("common.preferences.visibleContent.title"),
                            options: [{
                                label: getString("common.preferences.visibleContent.title"),
                                options: VISIBLE_CONTENT_OPTIONS,
                            }],
                        }}
                    />
                }
            />
            {showSelectedOnly && (
                <Box textAlign="center" color="text-status-info" fontSize="body-s">
                    {getString("bulkOrders.submitModal.showSelectedOnlyIndicator")}
                </Box>
            )}
        </SpaceBetween>
    );
};

// Modal

export interface AddBulkOrdersResult {
    vendor: string;
    templates: BulkOrderSource[];
}

export interface AddBulkOrdersModalProps {
    visible: boolean;
    onDismiss: () => void;
    onSubmit: (result: AddBulkOrdersResult) => void;
}

export const AddBulkOrdersModal: FunctionComponent<AddBulkOrdersModalProps> = ({
    visible,
    onDismiss,
    onSubmit,
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [selectedVendor, setSelectedVendor] = useState<string>("");
    const [selectedItems, setSelectedItems] = useState<BulkOrderSource[]>([]);

    const reset = () => {
        setStep(1);
        setSelectedVendor("");
        setSelectedItems([]);
    };

    const handleDismiss = () => {
        reset();
        onDismiss();
    };

    const selectVendor = (vendor: string) => {
        setSelectedVendor(vendor);
        setSelectedItems([]);
        setStep(2);
    };

    const handleVendorChange = (vendor: string) => {
        setSelectedVendor(vendor);
        setSelectedItems([]);
    };

    const handleSubmit = () => {
        if (!selectedVendor || selectedItems.length === 0) return;
        onSubmit({ vendor: selectedVendor, templates: selectedItems });
        reset();
    };

    return (
        <Modal
            data-testid="add-bulk-orders-modal"
            visible={visible}
            onDismiss={handleDismiss}
            size="max"
            header={getString("bulkOrders.submitModal.title")}
            footer={
                step === 2 ? (
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button data-testid="add-bulk-orders-cancel" onClick={handleDismiss}>{getString("common.cancel")}</Button>
                            <Button
                                data-testid="add-bulk-orders-submit"
                                variant="primary"
                                disabled={selectedItems.length === 0}
                                onClick={handleSubmit}
                            >
                                {getString("bulkOrders.submitModal.submit")} ({selectedItems.length})
                            </Button>
                        </SpaceBetween>
                    </Box>
                ) : undefined
            }
        >
            {step === 1 && <VendorSelectionStep onSelectVendor={selectVendor} />}
            {step === 2 && (
                <TemplateSelectionStep
                    selectedVendor={selectedVendor}
                    selectedItems={selectedItems}
                    onVendorChange={handleVendorChange}
                    onSelectionChange={setSelectedItems}
                />
            )}
        </Modal>
    );
};
